import { CalendarToday } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import WatchLaterOutlinedIcon from '@mui/icons-material/WatchLaterOutlined';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import loader from 'src/components/LoaderImg/loader.gif';
import { BasicDateCalendar } from 'src/layouts/components/CustomDatePicker';
import codeIcon from '../../icons/untitled-ui/duocolor/code.svg';
import goalIcon from '../../icons/untitled-ui/duocolor/goal.svg';
import PatientListing from './patientListing';

import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { Container } from '@mui/system';
import axios from 'axios';
import type { SVGProps } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useCurrentUser from 'src/hooks/use-current-user';
import { useSettings } from 'src/hooks/use-settings';
import { getSameDateIgnoringTimezone } from 'src/layouts/components/utils-method';
import {
  getDailySessionNotes,
  getDailySessionPatientInfoV2,
  resetDailyNotes,
  resetDailySessionOtherPatientList,
  resetDailySessionPatientList,
  resetDailySessionSoapNotes,
  updateDailySessionSoapNotes,
} from 'src/redux/actions/dailySessionNotes';
import {
  getAllTherapistAction,
  getTherapistInfoAction,
  resetDashboardDocumentESign,
  resetTherapistInfo,
  resetUndoDocumentSignAction,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import { getLockDailySessionNoteAction } from 'src/redux/actions/unlockTherapistAction';
import { useScopeChecker } from 'src/rbac';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { complianceOnBehalf } from 'src/config';
import {
  COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE,
  isComplianceCreateMode,
} from 'src/components/document-management/compliance-document-on-behalf';
import OnBehalfReasonModal from 'src/components/document-management/on-behalf-reason-modal';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import { Seo } from '../seo';
import { ignoreGroup } from 'src/layouts/components/utils-method';
import FinalizeNotes from './finalise-notes';
import ViewSOAPDocument from './view-soap-notes';
import PatientGoalInfo from 'src/pages/PatientGoalInfo';
import useIsDateInClosedPeriod from './useIsDateInClosedPeriod';
import ClosedPeriodDialog from './ClosedPeriodDialog';
// THIN-7916: facility-timezone-aware "today" for the default date of service + future-date guard.
import { getFacilityToday } from 'src/utils/convertDateToSelectedTimeZone';
import { dsnPageTestId } from './test-ids';

const EvaluationIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M2.8125 2.25V10.6875C2.8125 11.1351 2.99029 11.5643 3.30676 11.8807C3.62323 12.1972 4.05245 12.375 4.5 12.375H6.1875M2.8125 2.25H1.6875M2.8125 2.25H15.1875M6.1875 12.375H11.8125M6.1875 12.375L5.4375 14.625M15.1875 2.25H16.3125M15.1875 2.25V10.6875C15.1875 11.1351 15.0097 11.5643 14.6932 11.8807C14.3768 12.1972 13.9476 12.375 13.5 12.375H11.8125M11.8125 12.375L12.5625 14.625M5.4375 14.625H12.5625M5.4375 14.625L5.0625 15.75M12.5625 14.625L12.9375 15.75M5.625 9L7.875 6.75L9.486 8.361C10.2429 7.27447 11.2291 6.36737 12.375 5.70375"
      stroke="#6C737F"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TherapyIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M11.7495 2.916C11.6495 2.56223 11.4367 2.25079 11.1435 2.02904C10.8503 1.80729 10.4926 1.68736 10.125 1.6875H7.875C7.1025 1.6875 6.45 2.20725 6.2505 2.916M11.7495 2.916C11.7908 3.0615 11.8125 3.216 11.8125 3.375C11.8125 3.52418 11.7532 3.66726 11.6477 3.77275C11.5423 3.87824 11.3992 3.9375 11.25 3.9375H6.75C6.60082 3.9375 6.45774 3.87824 6.35225 3.77275C6.24676 3.66726 6.1875 3.52418 6.1875 3.375C6.1875 3.216 6.21 3.0615 6.2505 2.916M11.7495 2.916C12.234 2.95275 12.7155 2.9985 13.1948 3.054C14.0198 3.15 14.625 3.86175 14.625 4.69275V14.625C14.625 15.0726 14.4472 15.5018 14.1307 15.8182C13.8143 16.1347 13.3851 16.3125 12.9375 16.3125H5.0625C4.61495 16.3125 4.18573 16.1347 3.86926 15.8182C3.55279 15.5018 3.375 15.0726 3.375 14.625V4.69275C3.375 3.86175 3.9795 3.15 4.80525 3.054C5.28603 2.99833 5.76787 2.95232 6.2505 2.916"
      stroke="#6C737F"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.375 10.125H11.625M9 7.5V12.75"
      stroke="#6C737F"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GroupTherapyIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M13.0329 11.5533C13.3838 11.3137 13.5566 11.2658 13.9812 11.2503C14.4057 11.2349 14.826 11.3399 15.1934 11.5533C15.5607 11.7667 15.8601 12.0798 16.057 12.4563C16.2538 12.8328 16.3401 13.2573 16.3057 13.6807C15.4059 13.9953 14.4501 14.1176 13.5 14.04C13.4971 13.19 13.3273 11.9677 12.8692 11.2516C12.4627 10.6139 11.8268 9.91653 11.1636 9.55313C10.5004 9.18973 9.75624 8.99949 8.99999 9C8.24387 8.99961 7.49986 9.18992 6.83679 9.55331C6.17371 9.9167 5.53723 10.614 5.13073 11.2516M13.4992 14.0392L13.5 14.0625C13.5 14.2312 13.491 14.3978 13.4722 14.562C12.1113 15.3428 10.5691 15.7525 8.99999 15.75C7.37249 15.75 5.84474 15.318 4.52774 14.562C4.50846 14.3884 4.49919 14.2139 4.49999 14.0392M4.49999 14.0392C3.55021 14.1197 2.59492 13.9978 1.69574 13.6815C1.6615 13.2582 1.74778 12.8338 1.94461 12.4574C2.14144 12.0811 2.44077 11.7681 2.80799 11.5547C3.17521 11.3413 3.59534 11.2362 4.01978 11.2516C4.44422 11.267 4.87482 11.5533 5.13073 11.2516M4.49999 14.0392C4.50268 13.1893 4.67295 11.9677 5.13073 11.2516M11.25 5.0625C11.25 5.65924 11.0129 6.23153 10.591 6.65349C10.169 7.07545 9.59673 7.3125 8.99999 7.3125C8.40325 7.3125 7.83096 7.07545 7.409 6.65349C6.98704 6.23153 6.74999 5.65924 6.74999 5.0625C6.74999 4.46576 6.98704 3.89347 7.409 3.47151C7.83096 3.04955 8.40325 2.8125 8.99999 2.8125C9.59673 2.8125 10.169 3.04955 10.591 3.47151C11.0129 3.89347 11.25 4.46576 11.25 5.0625ZM15.75 7.3125C15.75 7.53411 15.7063 7.75354 15.6215 7.95828C15.5367 8.16302 15.4124 8.34904 15.2557 8.50574C15.099 8.66244 14.913 8.78674 14.7083 8.87155C14.5035 8.95635 14.2841 9 14.0625 9C13.8409 9 13.6214 8.95635 13.4167 8.87155C13.212 8.78674 13.0259 8.66244 12.8692 8.50574C12.7125 8.34904 12.5882 8.16302 12.5034 7.95828C12.4186 7.75354 12.375 7.53411 12.375 7.3125C12.375 6.86495 12.5528 6.43573 12.8692 6.11926C13.1857 5.80279 13.6149 5.625 14.0625 5.625C14.51 5.625 14.9393 5.80279 15.2557 6.11926C15.5722 6.43573 15.75 6.86495 15.75 7.3125ZM5.62499 7.3125C5.62499 7.53411 5.58134 7.75354 5.49654 7.95828C5.41173 8.16302 5.28743 8.34904 5.13073 8.50574C4.97403 8.66244 4.788 8.78674 4.58327 8.87155C4.37853 8.95635 4.15909 9 3.93749 9C3.71588 9 3.49645 8.95635 3.29171 8.87155C3.08697 8.78674 2.90094 8.66244 2.74425 8.50574C2.58755 8.34904 2.46325 8.16302 2.37844 7.95828C2.29364 7.75354 2.24999 7.53411 2.24999 7.3125C2.24999 6.86495 2.42778 6.43573 2.74425 6.11926C3.06071 5.80279 3.48994 5.625 3.93749 5.625C4.38504 5.625 4.81426 5.80279 5.13073 6.11926C5.4472 6.43573 5.62499 6.86495 5.62499 7.3125Z"
      stroke="#6C737F"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 12H11M9 10.5V13.5"
      stroke="#6C737F"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const InsuranceCallIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M1.6875 5.0625C1.6875 11.2755 6.7245 16.3125 12.9375 16.3125H14.625C15.0726 16.3125 15.5018 16.1347 15.8182 15.8182C16.1347 15.5018 16.3125 15.0726 16.3125 14.625V13.596C16.3125 13.209 16.0493 12.8715 15.6735 12.7778L12.3563 11.9482C12.0263 11.8657 11.6798 11.9895 11.4765 12.261L10.749 13.2308C10.5375 13.5128 10.1722 13.6372 9.8415 13.5157C8.61366 13.0643 7.49861 12.3514 6.57359 11.4264C5.64856 10.5014 4.93566 9.38634 4.48425 8.1585C4.36275 7.82775 4.48725 7.4625 4.76925 7.251L5.739 6.5235C6.01125 6.32025 6.13425 5.973 6.05175 5.64375L5.22225 2.3265C5.1766 2.14402 5.07129 1.98203 4.92304 1.86625C4.77479 1.75048 4.5921 1.68756 4.404 1.6875H3.375C2.92745 1.6875 2.49822 1.86529 2.18176 2.18176C1.86529 2.49822 1.6875 2.92745 1.6875 3.375V5.0625Z"
      stroke="#6C737F"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ScreeningsIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M8.25 5.45833H12M8.25 8.625H12M8.25 11.7917H12M7.5 1.5H4.125C3.504 1.5 3 2.032 3 2.6875V14.5625C3 15.218 3.504 15.75 4.125 15.75H13.875C14.496 15.75 15 15.218 15 14.5625V2.6875C15 2.032 14.496 1.5 13.875 1.5H7.5ZM6 5.45833H6.008V5.46678H6V5.45833ZM6 8.625H6.008V8.63345H6V8.625ZM6 11.7917H6.008V11.8001H6V11.7917Z"
      stroke="#6C737F"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ConcurrentIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 15 16"
    fill="none"
  >
    <path
      d="M10.4811 10.712C10.8709 10.4458 11.0629 10.3926 11.5347 10.3754C12.0064 10.3582 12.4734 10.4749 12.8816 10.712C13.2897 10.9491 13.6224 11.297 13.8412 11.7153C14.0599 12.1336 14.1557 12.6053 14.1176 13.0758C13.1177 13.4253 12.0557 13.5613 11.0001 13.475C10.9968 12.5305 10.8082 11.1724 10.2992 10.3768C9.84748 9.66826 9.14098 8.89336 8.40408 8.48959C7.66717 8.08581 6.84033 7.87443 6.00005 7.875C5.15992 7.87457 4.33324 8.08602 3.59649 8.48979C2.85975 8.89356 2.15255 9.66838 1.70088 10.3768C1.19223 11.1725 1.00305 12.5298 1.00005 13.4742C0.999166 13.6682 1.00946 13.8621 1.03089 14.055C2.49422 14.895 4.19172 15.375 6.00005 15.375C7.74346 15.3778 9.45702 14.9226 10.9692 14.055C10.9901 13.8725 11.0001 13.6875 11.0001 13.5L10.9992 13.4742M8.50005 3.5C8.50005 4.16304 8.23666 4.79893 7.76782 5.26777C7.29898 5.73661 6.66309 6 6.00005 6C5.33701 6 4.70113 5.73661 4.23229 5.26777C3.76344 4.79893 3.50005 4.16304 3.50005 3.5C3.50005 2.83696 3.76344 2.20107 4.23229 1.73223C4.70113 1.26339 5.33701 1 6.00005 1C6.66309 1 7.29898 1.26339 7.76782 1.73223C8.23666 2.20107 8.50005 2.83696 8.50005 3.5ZM13.5001 6C13.5001 6.24623 13.4516 6.49005 13.3573 6.71753C13.2631 6.94502 13.125 7.15171 12.9509 7.32582C12.7768 7.49993 12.5701 7.63805 12.3426 7.73227C12.1151 7.8265 11.8713 7.875 11.6251 7.875C11.3788 7.875 11.135 7.8265 10.9075 7.73227C10.68 7.63805 10.4733 7.49993 10.2992 7.32582C10.1251 7.15171 9.98701 6.94502 9.89278 6.71753C9.79855 6.49005 9.75005 6.24623 9.75005 6C9.75005 5.50272 9.9476 5.02581 10.2992 4.67417C10.6509 4.32254 11.1278 4.125 11.6251 4.125C12.1223 4.125 12.5992 4.32254 12.9509 4.67417C13.3025 5.02581 13.5001 5.50272 13.5001 6Z"
      stroke="#111927"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.5 11.75H7.5M6 10.5V13"
      stroke="#111927"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const iconList = {
  EVALUATION: {
    name: 'Evaluation',
    icon: <EvaluationIcon />,
  },
  THERAPY: {
    name: 'Therapy',
    icon: <TherapyIcon />,
  },
  GROUP_THERAPY: {
    name: 'Group Therapy',
    icon: <GroupTherapyIcon />,
  },
  INSURANCE_CALL: {
    name: 'Insurance call',
    icon: <InsuranceCallIcon />,
  },
  SCREENINGS: {
    name: 'Screenings',
    icon: <ScreeningsIcon />,
  },

  CONCURRENT: {
    name: 'Concurrent',
    icon: <ConcurrentIcon />,
  },
};

const DailySessionNotes = () => {
  const [openCalender, setOpenCalender] = useState(false);
  // THIN-7916: default to the facility-local day, not the browser/UTC day (which rolls to "tomorrow"
  // in the evening for US Central/Eastern facilities and dated evening notes a day ahead).
  const [selectedDate, setSelectedDate] = useState<any>(getFacilityToday());
  const calenderRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const providerId = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const [discipline] = useCurrentUser(['discipline']);
  const therapyInfoV2 = useSelector((state: any) => state.getDailySessionPatientInfoV2.response);
  const therapyInfoResponse2 = useSelector((state: any) => state.getDailySessionPatientInfoV2);
  const therapistProfileData = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );

  const dailySessionNotesInfoResponse = useSelector((state: any) => state?.getDailySessionNotes);
  const dailySessionNotesInfo = useSelector((state: any) => state?.getDailySessionNotes?.response);
  const [isOpen, setIsOpen] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const isDocLoader = useSelector((state: any) => state.loaderDocumenattionReducer);
  const checkSignDailyNotes = useSelector((state: any) => state.checkSignatureDailyNotesReducer);
  const soapNotesResponse = useSelector((state: any) => state?.updateSoapNoteReducer);
  const onClose = () => {
    setOpen(false);
  };
  const [openEditNotes, setOpenEditNotes] = useState(false);
  const [closedPeriodDialogOpen, setClosedPeriodDialogOpen] = useState(false);
  const { isInClosedPeriod } = useIsDateInClosedPeriod(selectedDate);
  const dispatch = useDispatch();
  const selectedPatientName = `${selectedPatient?.lastName} ${selectedPatient?.firstName}`;

  //  BRK-2075: Start Note on behalf (epic BRK-2055; backend = BRK-2072). Page-level gate = the
  //  epic's single Split (fail-closed while loading) + the COMPLIANCE_DOCUMENT_ON_BEHALF scope.
  //  A picked target re-keys the whole page — patient lists, lock check, notes fetch — and
  //  Start Note collects the mandatory reason, then dispatches the create TARGET-keyed with
  //  onBehalfReason (the discriminator no legacy client sends). Without the scope/Split the
  //  selector does not exist and every dispatch below is byte-identical to today.
  const has = useScopeChecker();
  const [isComplianceOnBehalf] = useFeatureToggleWhenReady(complianceOnBehalf, 'ENTERPRISE');
  const complianceDsnMode = isComplianceCreateMode({
    complianceSplitOn: isComplianceOnBehalf,
    hasDocumentScope: has(COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE),
  });
  const [onBehalfTargetState, setOnBehalfTargetState] = useState<any>(null);
  //  Fail-closed derivation: a stale pick is never USED while the mode is off…
  const onBehalfTarget = complianceDsnMode ? onBehalfTargetState : null;
  const effectiveProviderId = onBehalfTarget?.uuid ?? providerId;
  const effectiveDiscipline = onBehalfTarget?.discipline ?? discipline;
  const [startNoteReasonOpen, setStartNoteReasonOpen] = useState(false);
  const facilityTherapists = useSelector((state: any) => state.getAllTherapistReducer);

  //  …and CLEARED the moment the mode drops, so it cannot silently resurface when the mode
  //  returns (a facility switch resets the profile — the scope reads false for a moment — and
  //  the Split client re-keys; without this, the old facility's target would spring back with
  //  no new pick and re-key every query). Dropping the mode also closes the reason modal, whose
  //  confirm would otherwise dereference the now-null target. Mirrors a manual target clear.
  useEffect(() => {
    if (!complianceDsnMode && (onBehalfTargetState || startNoteReasonOpen)) {
      dispatch(resetDailySessionOtherPatientList());
      dispatch(resetDailySessionPatientList());
      setSelectedPatient(null);
      setOnBehalfTargetState(null);
      setStartNoteReasonOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceDsnMode]);

  useEffect(() => {
    if (complianceDsnMode) {
      //  ALL facility providers (the page has no discipline tabs); narrowed to treating
      //  therapists other than the caller below.
      dispatch(getAllTherapistAction(null, null, null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceDsnMode]);

  const onBehalfOptions = (facilityTherapists?.response?.data || []).filter(
    (p: any) => p?.discipline && p?.uuid !== providerId
  );

  const handlePickOnBehalfTarget = (_e: any, value: any) => {
    //  The lists on screen belong to the previous provider — same reset a date change performs.
    dispatch(resetDailySessionOtherPatientList());
    dispatch(resetDailySessionPatientList());
    setSelectedPatient(null);
    setOnBehalfTargetState(value);
  };

  const handleClickCalender = () => {
    setOpenCalender((prev: any) => !prev);
  };
  const jsDate = moment(selectedDate, 'DD-MM-YYYY').toDate();
  const formattedDate = moment(jsDate).format('MMM D, YYYY');
  // THIN-7916: "(Today)" reflects the facility-local day so an evening note is labeled correctly.
  const currentDate = moment(getFacilityToday());
  const handleChangeDate = (e: any) => {
    dispatch(resetDailySessionOtherPatientList());
    dispatch(resetDailySessionPatientList());
    setSelectedDate(new Date(e));
    setSelectedPatient(null);
    setOpenCalender(false);
  };

  useEffect(() => {
    if (location?.state?.document?.source === 'FROM_EOC') {
      const { dateOfService, patientEoc } = location?.state?.document;
      dispatch(resetDailySessionOtherPatientList());
      dispatch(resetDailySessionPatientList());
      setSelectedDate(getSameDateIgnoringTimezone(dateOfService));
      setSelectedPatient(patientEoc);
      setOpenCalender(false);
    }
  }, []);
  const facilityId = localStorage.getItem('facilityId');

  const undoDocumentSignResponse = useSelector((state: any) => state.undoDocumentSignReducer);

  useEffect(() => {
    if (undoDocumentSignResponse?.isSuccess) {
      setOpenEditNotes(true);
      dispatch(resetUndoDocumentSignAction());
    }
  }, [undoDocumentSignResponse]);

  useEffect(() => {
    if (openCalender) {
      const handleClickOutside = (event: any) => {
        if (calenderRef.current && !calenderRef.current.contains(event.target)) {
          handleClickCalender();
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [openCalender]);

  useEffect(() => {
    if (!!selectedDate && selectedPatient?.uuid) {
      dispatch(
        getLockDailySessionNoteAction({
          param: {
            localDate: moment(selectedDate).format('YYYY-MM-DD'),
            patientId: selectedPatient?.uuid,
            //  BRK-2075: the lock belongs to whoever the note will be authored by.
            discipline: effectiveDiscipline,
          },
        })
      );
    }
  }, [selectedDate, selectedPatient]);

  useEffect(() => {
    if (open) {
      dispatch(
        getDailySessionPatientInfoV2({
          providerId: effectiveProviderId,
          patientId: selectedPatient?.uuid,
          date: moment(selectedDate).format('YYYY-MM-DD'),
        })
      );
    }
  }, [open]);

  const [therapyTask] =
    therapyInfoV2?.taskInfo?.filter((info: any) => info?.assignedTask === 'THERAPY') || [];
  const [concurrentTask] =
    therapyInfoV2?.taskInfo?.filter((info: any) => info?.assignedTask === 'CONCURRENT') || [];
  const [groupTask] =
    therapyInfoV2?.taskInfo?.filter((info: any) => info?.assignedTask === 'GROUP_THERAPY') || [];

  useEffect(() => {
    setOpenEditNotes(false);
  }, [selectedPatient]);
  const source = axios.CancelToken.source();

  const callSessionNote = () => {
    if (selectedPatient?.name)
      dispatch(
        getDailySessionNotes(
          {
            patientId: selectedPatient?.uuid,
            providerId: effectiveProviderId,
            date: moment(selectedDate, 'DD-MM-YYYY')?.format('YYYY-MM-DD'),
          },
          source.token
        )
      );
    // else {
    //   if (!selectedPatient?.name && !selectedPatient?.notesId) {
    //     dispatch(
    //       updateDailySessionSoapNotes({
    //         patientId: selectedPatient?.uuid,
    //         params: {
    //           providerId,
    //           assignDate: moment(selectedDate, 'DD-MM-YYYY')?.format('YYYY-MM-DD'),
    //         },
    //       })
    //     );
    //   }
    // }
  };

  useEffect(() => {
    if (selectedDate && selectedPatient) {
      dispatch(resetDashboardDocumentESign());
      dispatch(resetUuidAction());
      dispatch(resetDailySessionSoapNotes());
      callSessionNote();
    }

    return () => {
      source.cancel();
    };
  }, [selectedPatient, selectedDate]);

  useEffect(() => {
    if (!isDocLoader.loader && isDocLoader.response === null) return;
    if (!isDocLoader.loader) {
      callSessionNote();
    }
  }, [isDocLoader]);

  useEffect(() => {
    if (!selectedPatient) {
      dispatch(resetDailyNotes());
    }
  }, [selectedPatient]);

  useEffect(() => {
    if (!facilityId || location?.state?.document?.source === 'FROM_EOC') return;
    setSelectedPatient(null);
  }, [facilityId]);

  useEffect(() => {
    if (checkSignDailyNotes?.isCheckSign) {
      callSessionNote();
    }
  }, [checkSignDailyNotes]);

  // Clear stale UUID state when patient changes to prevent cross-contamination
  useEffect(() => {
    if (selectedPatient?.uuid) {
      dispatch(resetUuidAction());
    }
  }, [selectedPatient?.uuid, dispatch]);

  const otherPatientSoapNotes = useMemo(() => {
    return {
      dailySessionNote: {
        status: selectedPatient?.status,
        uuid: selectedPatient?.notesId,
      },
      prefillKey: selectedPatient?.prefillKey,
    };
  }, [selectedPatient]);
  const otherPatientSoapNotesResponse = useMemo(() => {
    return {
      loading: false,
      status: selectedPatient?.status,
      dailySessionNote: {
        uuid: selectedPatient?.notesId,
        status: selectedPatient?.status,
      },
      response: {
        pdfUrl: selectedPatient?.pdfUrl,
        formSubmissionId: selectedPatient?.formSubmissionId,
        dailySessionNote: {
          submissionId: selectedPatient?.submissionId,
        },
      },
      prefillKey: selectedPatient?.prefillKey,
    };
  }, [selectedPatient]);

  const createSoapNotes = () => {
    if (isInClosedPeriod) {
      setClosedPeriodDialogOpen(true);
      return;
    }
    //  BRK-2075: acting on behalf — collect the mandatory reason first. Reset the page-global
    //  identity lookup so the consent card can never show a previous lookup's therapist.
    if (onBehalfTarget) {
      dispatch(resetTherapistInfo());
      dispatch(getTherapistInfoAction({ providerId: onBehalfTarget.uuid }));
      setStartNoteReasonOpen(true);
      return;
    }
    dispatch(
      updateDailySessionSoapNotes({
        patientId: selectedPatient?.uuid,
        params: {
          providerId,
          assignDate: moment(selectedDate, 'DD-MM-YYYY')?.format('YYYY-MM-DD'),
        },
      })
    );
  };

  const confirmStartNoteOnBehalf = (_document: any, reason: string) => {
    setStartNoteReasonOpen(false);
    //  The mode can drop while the modal is open (the cleanup effect closes it, but a queued
    //  click may still land) — never dispatch without a live target.
    if (!onBehalfTarget?.uuid) {
      return;
    }
    dispatch(
      updateDailySessionSoapNotes({
        patientId: selectedPatient?.uuid,
        params: {
          //  TARGET-keyed: the note row is created under the target with the target's
          //  discipline/certification (BRK-2072 derives both server-side); onBehalfReason is the
          //  discriminator that makes the backend verify the grant and stamp
          //  created_on_behalf_reason.
          providerId: onBehalfTarget.uuid,
          assignDate: moment(selectedDate, 'DD-MM-YYYY')?.format('YYYY-MM-DD'),
          onBehalfReason: reason,
        },
      })
    );
  };

  const settings = useSettings();
  return (
    <>
      <Seo title="Restore-Insights: Dashboard" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // py: 8,
          py: 3,
        }}
      >
        <Container
          sx={{
            height: '100%',
          }}
          maxWidth={settings.stretch ? false : 'xl'}
        >
          <>
            <Box
              sx={{
                height: '100%',
              }}
            >
              <Stack
                flexDirection="row"
                gap="2rem"
                alignItems="center"
                sx={{ padding: '0 1.5rem' }}
              >
                <Typography variant="h4">Daily Session Notes</Typography>
                <Stack ref={calenderRef}>
                  <Button
                    data-testid={dsnPageTestId('date-picker')}
                    sx={{
                      background: '#fff',
                      borderRadius: '12px',
                      border: '1px solid rgba(17, 25, 39, 0.16)',
                      color: '#384250',
                      '&:hover': {
                        border: '1px solid rgba(17, 25, 39, 0.16)',
                      },
                    }}
                    startIcon={
                      <SvgIcon>
                        <CalendarToday />
                      </SvgIcon>
                    }
                    variant="outlined"
                    onClick={handleClickCalender}
                  >
                    {formattedDate} {moment(jsDate).isSame(currentDate, 'day') && '(Today)'}
                  </Button>
                  {openCalender && (
                    <BasicDateCalendar
                      value={selectedDate}
                      handleChangeDate={handleChangeDate}
                      zindexOverride={1101}
                      // THIN-7916: cannot pick a date of service after the facility-local day.
                      // JS Date to match the AdapterDateFns convention used across the app.
                      maxDate={getFacilityToday()}
                    />
                  )}
                </Stack>
                {/* BRK-2075: the on-behalf target re-keys the page; Start Note collects the
                    mandatory reason */}
                {complianceDsnMode && (
                  <Autocomplete
                    sx={{ minWidth: 320 }}
                    options={onBehalfOptions}
                    getOptionLabel={(option: any) =>
                      option?.discipline ? `${option?.name} (${option?.discipline})` : option?.name
                    }
                    value={onBehalfTarget}
                    onChange={handlePickOnBehalfTarget}
                    isOptionEqualToValue={(option: any, value: any) => option?.uuid === value?.uuid}
                    disabled={facilityTherapists?.loading || onBehalfOptions.length === 0}
                    renderInput={(params): JSX.Element => (
                      <TextField
                        {...params}
                        label={
                          !facilityTherapists?.loading && onBehalfOptions.length === 0
                            ? 'No Records Found'
                            : 'Acting on behalf of'
                        }
                        name="onBehalfTarget"
                      />
                    )}
                  />
                )}
              </Stack>
              <Divider
                sx={{
                  margin: '2rem auto 0 auto',
                  width: '98%',
                  borderColor: '#E5E7EB',
                  // minWidth: isOpen ? '1500px' : '1200px',
                }}
              />
              <Stack
                gap="1rem"
                flexDirection="row"
                sx={{
                  padding: '1.5rem',
                  paddingTop: '0rem',
                  height: '90%',
                  // minWidth: isOpen ? '1500px' : '1200px',
                }}
              >
                <Stack
                  sx={{
                    flex: isOpen ? 2 : 0,
                    paddingTop: '1.5rem',
                    height: '100%',
                    width: isOpen ? '100%' : '80px',
                  }}
                >
                  <PatientListing
                    isOpen={isOpen}
                    selectedDate={selectedDate}
                    setIsOpen={setIsOpen}
                    selectedPatient={selectedPatient}
                    setSelectedPatient={setSelectedPatient}
                    onBehalfTarget={onBehalfTarget}
                  />
                </Stack>
                <Divider sx={{ height: '100%', border: '1px solid #E5E7EB' }} />
                <Stack sx={{ flex: 8, paddingTop: '1rem' }}>
                  {dailySessionNotesInfoResponse?.loading ? (
                    <img
                      src={loader}
                      alt="loader"
                      style={{
                        width: '150px',
                        margin: 'auto',
                        alignContent: 'center',
                        marginTop: '30%',
                        display: 'block',
                      }}
                    />
                  ) : ((selectedPatient?.name &&
                      (dailySessionNotesInfoResponse.response?.dailySessionNote?.status ===
                        'FINALIZED' ||
                        dailySessionNotesInfoResponse.response?.dailySessionNote?.status ===
                          'SIGNED')) ||
                      (!selectedPatient?.name &&
                        (selectedPatient?.status === 'FINALIZED' ||
                          selectedPatient?.status === 'SIGNED'))) &&
                    !openEditNotes ? (
                    <ViewSOAPDocument
                      dailySessionNotesInfoResponse={
                        selectedPatient?.name
                          ? dailySessionNotesInfoResponse
                          : otherPatientSoapNotesResponse
                      }
                      selectedPatient={selectedPatient}
                      setOpen={setOpen}
                      selectedDate={selectedDate}
                      setOpenEditNotes={setOpenEditNotes}
                      isInClosedPeriod={isInClosedPeriod}
                      onClosedPeriodAction={() => setClosedPeriodDialogOpen(true)}
                      //  BRK-2086: null outside compliance mode (and cleared on mode drop), so
                      //  the signed-note pane's Sign Document forks to the proxy flow only for
                      //  an active on-behalf pick.
                      onBehalfTarget={onBehalfTarget}
                    />
                  ) : (
                    <FinalizeNotes
                      dailySessionNotesInfo={
                        selectedPatient?.name ? dailySessionNotesInfo : otherPatientSoapNotes
                      }
                      selectedPatient={selectedPatient}
                      setOpen={setOpen}
                      dailySessionNotesInfoResponse={{
                        response: {
                          dailySessionNote: {
                            status: selectedPatient?.status,
                            uuid: selectedPatient?.notesId,
                          },
                          prefillKey: selectedPatient?.prefillKey,
                        },
                        loading: false,
                      }}
                      setOpenEditNotes={setOpenEditNotes}
                      createSoapNotes={createSoapNotes}
                      selectedDate={moment(selectedDate)?.format()}
                      isInClosedPeriod={isInClosedPeriod}
                      onClosedPeriodAction={() => setClosedPeriodDialogOpen(true)}
                    />
                  )}
                </Stack>
              </Stack>
            </Box>
            <Drawer
              anchor="right"
              onClose={onClose}
              open={open}
              PaperProps={{
                sx: {
                  width: '100%',
                  maxWidth: 500,
                },
              }}
            >
              <Stack>
                <Stack
                  justifyContent="space-between"
                  sx={{ margin: '1.5rem', gap: '2rem' }}
                  flexDirection="row"
                >
                  <Typography
                    sx={{ height: 'max-content', wordBreak: 'break-word' }}
                    variant="h5"
                  >
                    {selectedPatientName
                      ? selectedPatientName?.endsWith("'s")
                        ? selectedPatientName
                        : `${selectedPatientName}'s`
                      : ''}{' '}
                    Info
                  </Typography>
                  <IconButton
                    sx={{ padding: '0px' }}
                    data-testid={dsnPageTestId('therapy-info-close')}
                    onClick={() => setOpen(false)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
                <Divider />
                {!therapyInfoResponse2?.loading && (
                  <Stack sx={{ padding: '1rem' }}>
                    {therapyTask && (
                      <>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          sx={{ margin: '1rem' }}
                        >
                          <Stack
                            flexDirection="row"
                            gap="1rem"
                            alignItems={therapyTask?.groupName === 'N/A' ? 'center' : 'start'}
                            sx={{ width: '60%' }}
                          >
                            <Stack
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                borderRadius: '12px',
                                backgroundColor: '#F5F7FF',
                                width: '45px',
                                height: '42px',
                              }}
                            >
                              {(iconList as any)[therapyTask?.assignedTask || '']?.icon}
                            </Stack>
                            <Stack gap=".3rem">
                              <Typography variant="captionText">
                                {(iconList as any)[therapyTask?.assignedTask || '']?.name}
                              </Typography>
                              {therapyTask?.groupName !== 'N/A' && (
                                <Stack
                                  sx={{
                                    backgroundColor: '#ECFDFF',
                                    color: '#06AED4',
                                    borderRadius: '12px',
                                    padding: '8px',
                                    maxWidth: 'max-content',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: '#06AED4',
                                      maxWidth: 'max-content',
                                    }}
                                    variant="caption3Text"
                                  >
                                    {therapyTask?.groupName}
                                  </Typography>
                                </Stack>
                              )}
                              {therapyTask?.groupName === 'N/A' && (
                                <Typography
                                  sx={{
                                    color: '#111927',
                                    maxWidth: 'max-content',
                                    margin: '0',
                                  }}
                                  variant="body2"
                                >
                                  Individual therapy session
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                          <Stack
                            flexDirection="row"
                            gap="1rem"
                            sx={{ marginRight: '2.5rem', width: '35%' }}
                          >
                            <Stack
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                borderRadius: '12px',
                                backgroundColor: '#F5F7FF',
                                width: '45px',
                                height: '42px',
                              }}
                            >
                              <WatchLaterOutlinedIcon />
                            </Stack>
                            <Stack gap=".3rem">
                              <Typography variant="captionText">Duration</Typography>
                              <Typography variant="caption3Text">{therapyTask?.time}</Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                        <Divider />{' '}
                      </>
                    )}
                    {concurrentTask && (
                      <>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          sx={{ margin: '1rem' }}
                        >
                          <Stack
                            flexDirection="row"
                            gap="1rem"
                            alignItems={concurrentTask?.groupName === 'N/A' ? 'center' : 'start'}
                            sx={{ width: '60%' }}
                          >
                            <Stack
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                borderRadius: '12px',
                                backgroundColor: '#F5F7FF',
                                width: '45px',
                                height: '42px',
                              }}
                            >
                              {(iconList as any)[concurrentTask?.assignedTask || '']?.icon}
                            </Stack>
                            <Stack gap=".3rem">
                              <Typography
                                sx={{ marginLeft: '4px' }}
                                variant="captionText"
                              >
                                {(iconList as any)[concurrentTask?.assignedTask || '']?.name}
                              </Typography>
                              {concurrentTask?.groupName !== 'N/A' && (
                                <Stack
                                  sx={{
                                    backgroundColor: '#ECFDFF',
                                    color: '#06AED4',
                                    borderRadius: '12px',
                                    padding: '8px',
                                    maxWidth: 'max-content',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: '#06AED4',
                                      maxWidth: 'max-content',
                                    }}
                                    variant="caption3Text"
                                  >
                                    {concurrentTask?.groupName}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                          </Stack>
                          <Stack
                            flexDirection="row"
                            gap="1rem"
                            sx={{ marginRight: '2.5rem', width: '35%' }}
                          >
                            <Stack
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                borderRadius: '12px',
                                backgroundColor: '#F5F7FF',
                                width: '45px',
                                height: '42px',
                              }}
                            >
                              <WatchLaterOutlinedIcon />
                            </Stack>
                            <Stack gap=".3rem">
                              <Typography variant="captionText">Duration</Typography>
                              <Typography variant="caption3Text">{concurrentTask?.time}</Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                        <Divider />{' '}
                      </>
                    )}
                    {groupTask && (
                      <>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          sx={{ margin: '1rem' }}
                        >
                          <Stack
                            flexDirection="row"
                            gap="1rem"
                            alignItems={groupTask?.groupName === 'N/A' ? 'center' : 'start'}
                            sx={{ width: '60%' }}
                          >
                            <Stack
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                borderRadius: '12px',
                                backgroundColor: '#F5F7FF',
                                width: '45px',
                                height: '42px',
                              }}
                            >
                              {(iconList as any)[groupTask?.assignedTask || '']?.icon}
                            </Stack>
                            <Stack gap=".3rem">
                              <Typography
                                sx={{ marginLeft: '4px' }}
                                variant="captionText"
                              >
                                {(iconList as any)[groupTask?.assignedTask || '']?.name}
                              </Typography>
                              {groupTask?.groupName !== 'N/A' && (
                                <Stack
                                  sx={{
                                    backgroundColor: '#ECFDFF',
                                    color: '#06AED4',
                                    borderRadius: '12px',
                                    padding: '8px',
                                    maxWidth: 'max-content',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: '#06AED4',
                                      maxWidth: 'max-content',
                                    }}
                                    variant="caption3Text"
                                  >
                                    {groupTask?.groupName}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                          </Stack>
                          <Stack
                            flexDirection="row"
                            gap="1rem"
                            sx={{ marginRight: '2.5rem', width: '35%' }}
                          >
                            <Stack
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                borderRadius: '12px',
                                backgroundColor: '#F5F7FF',
                                width: '45px',
                                height: '42px',
                              }}
                            >
                              <WatchLaterOutlinedIcon />
                            </Stack>
                            <Stack gap=".3rem">
                              <Typography variant="captionText">Duration</Typography>
                              <Typography variant="caption3Text">{groupTask?.time}</Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                        <Divider />
                      </>
                    )}

                    <Divider />

                    <Stack
                      flexDirection="row"
                      gap="1rem"
                      sx={{ padding: '1rem' }}
                    >
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          borderRadius: '12px',
                          backgroundColor: '#F5F7FF',
                          width: '45px',
                          height: '42px',
                        }}
                      >
                        <img
                          width="24px"
                          height="24px"
                          src={goalIcon}
                          alt=""
                        />
                      </Stack>
                      <Stack gap=".3rem">
                        <Typography variant="captionText">Goals</Typography>
                        {(onBehalfTarget?.discipline ?? therapistProfileData?.discipline) ===
                        'PT' ? (
                          <Stack mt={1}>
                            <PatientGoalInfo
                              discipline="PT"
                              isShortTerm={true}
                              goals={therapyInfoV2?.shortTermGoals}
                              data={therapyInfoV2?.goalSkills}
                            />
                            <PatientGoalInfo
                              discipline="PT"
                              isShortTerm={false}
                              goals={therapyInfoV2?.longTermGoals}
                              data={therapyInfoV2?.goalSkills}
                            />
                          </Stack>
                        ) : null}
                        {(onBehalfTarget?.discipline ?? therapistProfileData?.discipline) ===
                        'OT' ? (
                          <Stack mt={1}>
                            <PatientGoalInfo
                              discipline="OT"
                              isShortTerm={true}
                              goals={therapyInfoV2?.shortTermGoals}
                              data={therapyInfoV2?.goalSkills}
                            />
                            <PatientGoalInfo
                              discipline="OT"
                              isShortTerm={false}
                              goals={therapyInfoV2?.longTermGoals}
                              data={therapyInfoV2?.goalSkills}
                            />
                          </Stack>
                        ) : null}
                        {(onBehalfTarget?.discipline ?? therapistProfileData?.discipline) ===
                        'SLP' ? (
                          <Stack mt={1}>
                            <PatientGoalInfo
                              discipline="SLP"
                              isShortTerm={true}
                              goals={therapyInfoV2?.shortTermGoals}
                              data={therapyInfoV2?.goalSkills}
                            />
                            <PatientGoalInfo
                              discipline="SLP"
                              isShortTerm={false}
                              goals={therapyInfoV2?.longTermGoals}
                              data={therapyInfoV2?.goalSkills}
                            />
                          </Stack>
                        ) : null}
                      </Stack>
                    </Stack>
                    <Divider />
                    <Stack
                      flexDirection="row"
                      gap="1rem"
                      sx={{ padding: '1rem' }}
                    >
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          borderRadius: '12px',
                          backgroundColor: '#F5F7FF',
                          width: '45px',
                          height: '42px',
                        }}
                      >
                        <ArticleOutlinedIcon />
                      </Stack>
                      <Stack gap=".3rem">
                        <Typography variant="captionText">Notes</Typography>
                        <Typography variant="caption3Text">
                          {therapyInfoV2?.noteFromLastSession
                            ? therapyInfoV2?.noteFromLastSession
                            : '-'}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Divider />
                    <Stack
                      flexDirection="row"
                      gap="1rem"
                      sx={{ padding: '1rem' }}
                    >
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          borderRadius: '12px',
                          backgroundColor: '#F5F7FF',
                          width: '45px',
                          height: '42px',
                        }}
                      >
                        <img
                          alt=""
                          src={codeIcon}
                        />
                      </Stack>
                      <Stack gap=".3rem">
                        <Typography variant="captionText">Codes</Typography>
                        <Stack
                          flexDirection="row"
                          gap="1rem"
                          flexWrap="wrap"
                        >
                          {Object.entries(therapyInfoV2?.cptCodeDescription || {})?.map(
                            ([code, description]: any, index: any) => (
                              <Chip
                                variant="outlined"
                                key={index}
                                label={
                                  <Typography variant="boldText">
                                    {code} | {description}
                                  </Typography>
                                }
                              />
                            )
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                )}
                {therapyInfoResponse2?.loading && (
                  <Stack
                    gap="1.5rem"
                    p={3}
                  >
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1 }}>
                      <Skeleton
                        animation={false}
                        variant="circular"
                      >
                        <Avatar sx={{ width: 70, height: 70 }} />
                      </Skeleton>
                      <Box sx={{ width: '100%', mt: 1 }}>
                        <Typography
                          width={'40%'}
                          variant="h5"
                        >
                          <Skeleton animation={false} />
                        </Typography>
                        <Typography
                          width={'30%'}
                          variant="h5"
                        >
                          <Skeleton animation={false} />
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        marginTop={2}
                        width={'100%'}
                        variant="h4"
                      >
                        <Skeleton animation={'wave'} />
                      </Typography>
                      <Typography
                        marginTop={1}
                        width={'100%'}
                        variant="h4"
                      >
                        <Skeleton animation={'wave'} />
                      </Typography>
                    </Box>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                      <Typography
                        width={'30%'}
                        variant="h1"
                      >
                        <Skeleton animation={false} />
                      </Typography>
                      <Typography
                        width={'30%'}
                        variant="h1"
                      >
                        <Skeleton animation={false} />
                      </Typography>
                    </Box>
                    {['1', '2', '3'].map((sle) => {
                      return (
                        <Box
                          sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
                          key={sle}
                        >
                          <Typography
                            width={'20%'}
                            variant="h1"
                          >
                            <Skeleton animation={false} />
                          </Typography>
                          <Typography
                            width={'75%'}
                            variant="h1"
                          >
                            <Skeleton animation={false} />
                          </Typography>
                        </Box>
                      );
                    })}
                    <Typography
                      marginTop={1}
                      width={'100%'}
                      variant="h1"
                    >
                      <Skeleton animation={'wave'} />
                    </Typography>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        width: '90%',
                      }}
                    >
                      <Typography
                        width={'100%'}
                        variant="h1"
                      >
                        <Skeleton animation={'wave'} />
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Stack>
            </Drawer>
          </>
        </Container>
      </Box>
      <ClosedPeriodDialog
        open={closedPeriodDialogOpen}
        onClose={() => setClosedPeriodDialogOpen(false)}
      />

      {/* BRK-2075: Start Note on behalf — mandatory reason before the note is created */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={() => setStartNoteReasonOpen(false)}
        open={startNoteReasonOpen}
      >
        {startNoteReasonOpen && (
          <OnBehalfReasonModal
            document={selectedPatient}
            onClose={() => setStartNoteReasonOpen(false)}
            onConfirm={confirmStartNoteOnBehalf}
            title="Starting a note on behalf of"
            actionPhrase="starting this daily session note"
            confirmLabel="Start Note"
          />
        )}
      </BootstrapDialog>
    </>
  );
};

export default DailySessionNotes;
